import { AudienceType, CommunicationAudienceType, RunAtType, SelectedStartType, TargetedAtType } from "../../config/enums";
import { GetDefaultValuesForEntity } from "../../api/entityService";
import { lookupRecordsCache } from "../../hooks/lookupRecords";
import { PageRecord, PageSaveOption, RecordAdapterContext, SaveConfirmation } from "../recordDefinition.types";
import { prepareTestAdditionalDataForSaveAction } from "./saveActionPayloadMapping";
import { hasSupportedCommunicationTestingControlInState } from "./testingControlDetection";
import {
  parseTestAdditionalData,
  toTestAdditionalDataXml,
} from "./communicationTestAdditionalDataModel";
import { convertUTCDateTimeToLocal, toDisplayDateTime } from "../../blocks/fields/formatDate";
import { isAutomationExecution, isPublished } from "./communicationRules";

const communicationDefaults: PageRecord = {
  g4c_communicationtype: CommunicationAudienceType.Marketing,
  g4c_regardingtype: "3",
  g4c_listtype: TargetedAtType.Contact,
  ToNameField: "firstname",
  ToAddressField: "emailaddress1",
};

const defaultRunAt = (config: RecordAdapterContext["config"]) =>
  Boolean(config?.WarehouseCommunicationAudienceEnabled) &&
  config?.EmailMarketingDefaultRunAt === RunAtType.Warehouse
    ? RunAtType.Warehouse
    : RunAtType.CRM;

/** A draft read without where it runs or when it is sent gets the defaults the legacy page filled in. */
export function adaptCommunicationRecord(record: PageRecord, { config }: RecordAdapterContext): PageRecord {
  return {
    ...record,
    ...(record.RunAt == null || record.RunAt === "" ? { RunAt: defaultRunAt(config) } : {}),
    ...(!isPublished(record) &&
    !record.g4c_starttype &&
    record.g4c_starttype !== 0 &&
    String(record.g4c_audiencetype) === String(AudienceType.Existing) &&
    !isAutomationExecution(record)
      ? { g4c_starttype: SelectedStartType.Immediately }
      : {}),
  };
}

/** The defaults, what the service sets for the organisation, and where it runs by default. */
export async function newCommunicationRecord({
  config,
  dispatch,
}: RecordAdapterContext): Promise<PageRecord> {
  let fromService: PageRecord = {};

  try {
    fromService = (await GetDefaultValuesForEntity(dispatch, "g4c_communication"))?.data ?? {};
  } catch {
    // The page still opens; it reports that ecomms is not activated.
  }

  return {
    ...communicationDefaults,
    ...fromService,
    RunAt: defaultRunAt(config),
  };
}

// What the lookups were read from, as `lookupRecordsCache` holds them.
const nameOf = (cacheKey: string, id: unknown, fallback: unknown) =>
  lookupRecordsCache[cacheKey]?.find((option) => String(option.Key) === String(id))
    ?.Value ??
  fallback ??
  "";

const publishedAttributes = ["g4c_name", "g4c_description", "g4c_campaignid"];

const draftAttributes = [
  ...publishedAttributes,
  "g4c_communicationtype",
  "RunAt",
  "g4c_audiencetype",
  "g4c_listtype",
  "g4c_audienceid",
  "g4c_regardingtype",
  "g4c_subject",
  "FromName",
  "FromAddress",
  "ToName",
  "ToNameField",
  "ToAddressField",
  "g4c_usedynamiccontent",
  "PlainTextContent",
  "g4c_testaddress",
  "g4c_testmarketinglistid",
  "g4c_starttype",
  "g4c_startdate",
  "g4c_enddate",
  "g4c_expirypage",
  "templateData",
  "templateHtml",
  "action",
  "finalCount",
  "testAdditionalData",
];

/** A published communication saves only its name, description and campaign. */
export function toCommunicationPayload(record: PageRecord): PageRecord {
  const names = isPublished(record) ? publishedAttributes : draftAttributes;
  const attributes: PageRecord = {
    ...Object.fromEntries(names.map((name) => [name, record[name]])),
    // The service reads the save intent from `saveAction`.
    saveAction: record.action ?? "save",
    g4c_campaignidname: nameOf("campaign", record.g4c_campaignid, record.g4c_campaignidname),
    ...(isPublished(record)
      ? {}
      : {
          g4c_audienceidname: nameOf(
            "g4c_communicationaudience",
            record.g4c_audienceid,
            record.g4c_audienceidname
          ),
          g4c_testmarketinglistidname: nameOf(
            "list",
            record.g4c_testmarketinglistid,
            record.g4c_testmarketinglistidname
          ),
        }),
  };

  // Basket test data goes only with a test send, and only for an email with a basket in it.
  prepareTestAdditionalDataForSaveAction(
    attributes,
    record.action,
    hasSupportedCommunicationTestingControlInState(record),
    () => toTestAdditionalDataXml(parseTestAdditionalData(record.testAdditionalData))
  );

  return attributes;
}

/** Adds the email's design as the email service reads it, worked out from what is about to be saved. */
export async function addTemplate(record: PageRecord): Promise<PageRecord> {
  if (isPublished(record)) {
    return record;
  }

  // Loaded here so the email renderers aren't behind everything that reads this file.
  const { templateDataOf, templateHtmlOf } = await import("./communicationTemplate");

  return { ...record, templateData: templateDataOf(record), templateHtml: templateHtmlOf(record) };
}

const isDraft = (record: PageRecord) => !isPublished(record);

// Sending to an audience with nobody in it, or one not yet counted, is refused by the service.
const audienceReason = (record: PageRecord) =>
  String(record.g4c_audiencetype) === String(AudienceType.Automation)
    ? undefined
    : record.audienceCounting
    ? "The audience is still being counted"
    : Number(record.finalCount) > 0
    ? undefined
    : "The audience needs a count above 0 before this can be sent";

/** Save and Test sends to the test address or list; Save and Publish sends for real. Each is the footer's save on its own tab. */
export const communicationSaveMenu: PageSaveOption[] = [
  {
    id: "test",
    scope: "test",
    label: "Save and Test",
    description: "Saves the communication and sends a test",
    patch: () => ({ action: "saveAndTest" }),
    // A test is sent now, whatever the publish step says.
    skipValidation: ["g4c_starttype", "g4c_startdate", "g4c_enddate", "g4c_expirypage"],
    successMessage: "Saved and test sent",
    visible: isDraft,
    disabledReason: audienceReason,
  },
  {
    id: "publish",
    scope: "publish",
    label: "Save and Publish",
    patch: () => ({ action: "saveAndPublish" }),
    successMessage: "Saved and published",
    visible: (record) => isDraft(record) && String(record.g4c_audiencetype) !== String(AudienceType["A/B Testing"]),
    disabledReason: audienceReason,
  },
];

/** What publishing says it will do, by how the communication is sent. */
export function communicationSaveConfirmation(record: PageRecord): SaveConfirmation | null {
  if (record.action !== "saveAndPublish") {
    return null;
  }

  const audience = String(record.g4c_audiencetype);
  const start = String(record.g4c_starttype);

  if (isAutomationExecution(record)) {
    const when = record.g4c_startdate
      ? toDisplayDateTime(convertUTCDateTimeToLocal(String(record.g4c_startdate))?.toDate())
      : "";

    return {
      title: "Publish Automation Communication",
      confirmText: "Publish for Automation",
      paragraphs: [
        `Do you want to publish this communication? It will be available for the automation send scheduled at ${when || record.g4c_startdate}.`,
      ],
    };
  }

  if (audience === String(AudienceType.Automation)) {
    return {
      title: "Publish Communication for Automation",
      confirmText: "Publish for Automation",
      paragraphs: [
        "Do you want to publish this communication? This action will attempt to publish this communication for use with an automation.",
      ],
    };
  }

  if (audience === String(AudienceType.Existing) && start === "0") {
    return {
      title: "Publish Communication Immediately",
      confirmText: "Publish Immediately",
      paragraphs: [
        "Do you want to publish this communication? This action will attempt to publish this communication immediately.",
      ],
    };
  }

  if (audience === String(AudienceType.Existing) && start === "1") {
    return {
      title: "Publish Scheduled Communication",
      confirmText: "Schedule Publish",
      paragraphs: ["Do you want to publish this communication?"],
    };
  }

  return { title: "Publish Communication", confirmText: "Confirm", paragraphs: ["Do you want to publish this communication?"] };
}
